import React, { useEffect, useRef, useState } from 'react';
import {
  bool, elementType, func, instanceOf, node, number, oneOf, oneOfType, shape, string
} from 'prop-types';
import classNames from 'classnames/bind';
import { isRefInViewport, isScrolledPastOffset } from '../helpers';
import styles from './sticky.module.scss';

const cx = classNames.bind(styles);

const Sticky = ({
  boxShadow,
  children,
  className,
  forwardRef,
  offset,
  position,
  stickyType,
  visibleByDefault,
  visibilityType
}) => {
  const [displaySticky, setDisplaySticky] = useState(visibleByDefault);
  const stickyRef = useRef(null);

  const shouldDisplayBasic = () => {
    const shouldDisplay = isScrolledPastOffset(offset, window);
    if (displaySticky !== shouldDisplay) {
      setDisplaySticky(shouldDisplay);
    }
  };

  const shouldDisplayRef = () => {
    const { offsetHeight } = forwardRef?.current;
    const forwardRefRect = forwardRef?.current.getBoundingClientRect();
    const stickyRefRect = stickyRef?.current.getBoundingClientRect();

    const isForwardRefInViewport = isRefInViewport({
      offset,
      refHeight: offsetHeight,
      refRect: forwardRefRect,
      windowObj: window
    });

    if (displaySticky !== !isForwardRefInViewport) {
      setDisplaySticky(!isForwardRefInViewport);
    }
  };

  const shouldDisplayOnScrollUp = () => {
    /*
      TODO - add new logic such that . . .
      sticky content is hidden on scroll down, but visible on scroll up
    */
    const shouldDisplay = isScrolledPastOffset(offset, window);
    if (displaySticky !== shouldDisplay) {
      setDisplaySticky(shouldDisplay);
    }
  };

  const handleScroll = () => {
    switch (stickyType) {
      case 'basic':
        shouldDisplayBasic();
        break;
      case 'forwardRef':
        shouldDisplayRef();
        break;
      case 'showOnScrollUp':
        shouldDisplayOnScrollUp();
        break;
      default:
        shouldDisplayBasic();
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [displaySticky]);

  const stickyClasses = classNames(
    cx('sticky'), className, {
      [cx('sticky--bottom')]: position === 'bottom',
      [cx('sticky--top')]: position === 'top',
      [cx('sticky__shadow--bottom')]: boxShadow && position === 'bottom',
      [cx('sticky__shadow--top')]: boxShadow && position === 'top',
      [cx('sticky__display')]: visibilityType === 'display',
      [cx('sticky__display--visible')]: visibilityType === 'display' && displaySticky,
      [cx('sticky__transform')]: visibilityType === 'transform',
      [cx('sticky__transform--visible')]: visibilityType === 'transform' && displaySticky
    }
  );

  return (
    <div className={stickyClasses} ref={stickyRef}>
      {children}
    </div>
  );
};

Sticky.displayName = 'Sticky';

Sticky.propTypes = {
  boxShadow: bool,
  children: node,
  className: string,
  // avoid propType error by allowing undefined prop type on ssr only
  forwardRef: oneOfType([
    func,
    shape({ current:
      instanceOf(
        typeof Element === 'undefined'
          ? function () { }
          : Element
      ) })
  ]),
  offset: number,
  position: oneOf(['bottom', 'top']).isRequired,
  stickyType: oneOf(['basic', 'forwardRef', 'showOnScrollUp']),
  visibilityType: oneOf(['display', 'transform']),
  visibleByDefault: bool
};

Sticky.defaultProps = {
  boxShadow: false,
  children: null,
  className: '',
  forwardRef: null,
  offset: 0,
  stickyType: 'basic',
  visibilityType: 'display',
  visibleByDefault: false
};

export { Sticky };
